import React, { useState } from 'react';
import { RefreshCw } from './Icons';
import './DeviceQueue.css';

const DeviceQueue = ({ queue, onRefresh, refreshing }) => {
  const [expandedItems, setExpandedItems] = useState({});

  if (!queue) {
    return (
      <div className="device-queue">
        <div className="empty-state">
          <p>No queue data available.</p>
        </div>
      </div>
    );
  }

  // Reverse completed and failed arrays to show most recent first
  // Ensure they're arrays before reversing
  const completedArray = Array.isArray(queue.completed) ? queue.completed : [];
  const failedArray = Array.isArray(queue.failed) ? queue.failed : [];
  const completedReversed = [...completedArray].reverse();
  const failedReversed = [...failedArray].reverse();

  const toggleExpanded = (section, index) => {
    const key = `${section}-${index}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatTimestamp = (timestampObj) => {
    if (!timestampObj) return 'N/A';
    if (timestampObj._seconds) {
      return new Date(timestampObj._seconds * 1000).toLocaleString();
    }
    return 'N/A';
  };

  const formatQueueItemSummary = (item) => {
    if (typeof item === 'string') return item;
    
    // Extract key fields from the object
    const id = item.id || item.media_id || 'Unknown';
    const activityId = item.activity_id ? ` (Activity: ${item.activity_id.substring(0, 8)}...)` : '';
    
    return `${id}${activityId}`;
  };

  const renderItemDetails = (item, type) => {
    if (typeof item === 'string') {
      return <div className="item-detail-simple">{item}</div>;
    }

    const isCompleted = type === 'completed';
    const isFailed = type === 'failed';

    return (
      <div className="item-details">
        <div className="detail-row">
          <span className="detail-label">Activity ID:</span>
          <span className="detail-value">{item.activity_id || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">User ID:</span>
          <span className="detail-value">{item.user_id || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Gate ID:</span>
          <span className="detail-value">{item.gate_id || 'N/A'}</span>
        </div>
        {isCompleted && (
          <div className="detail-row">
            <span className="detail-label">Media ID:</span>
            <span className="detail-value">{item.media_id || 'N/A'}</span>
          </div>
        )}
        <div className="detail-row">
          <span className="detail-label">Started At:</span>
          <span className="detail-value">{formatTimestamp(item.started_at)}</span>
        </div>
        {isCompleted && (
          <div className="detail-row">
            <span className="detail-label">Completed At:</span>
            <span className="detail-value">{formatTimestamp(item.completed_at)}</span>
          </div>
        )}
        {isFailed && (
          <>
            <div className="detail-row">
              <span className="detail-label">Failed At:</span>
              <span className="detail-value">{formatTimestamp(item.failed_at)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Failure Reason:</span>
              <span className="detail-value error">{item.failure_reason || 'N/A'}</span>
            </div>
          </>
        )}
        <div className="detail-row">
          <span className="detail-label">Concat. Video:</span>
          <span className="detail-value">{item.concatenated_video ? 'Yes' : 'No'}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Clip Start:</span>
          <span className="detail-value">{formatTimestamp(item.effective_slicing_timestamp)}</span>
        </div>
        {isCompleted && item.uploaded_url && (
          <div className="detail-row">
            <span className="detail-label">Video URL:</span>
            <span className="detail-value">
              <a href={item.uploaded_url} target="_blank" rel="noopener noreferrer" className="video-link">
                View Video →
              </a>
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderQueueSection = (title, items, className, sectionType) => {
    // Ensure items is always an array
    const itemsArray = Array.isArray(items) ? items : [];
    
    return (
      <div className={`queue-section ${className}`}>
        <h4>{title} ({itemsArray.length})</h4>
        {itemsArray.length === 0 ? (
          <div className="queue-empty">No items</div>
        ) : (
          <div className="queue-list">
            {itemsArray.map((item, index) => {
            const key = `${sectionType}-${index}`;
            const isExpanded = expandedItems[key];
            
            return (
              <div key={index} className={`queue-item ${isExpanded ? 'expanded' : ''}`}>
                <div 
                  className="queue-item-header"
                  onClick={() => toggleExpanded(sectionType, index)}
                >
                  <span className="expand-icon">{isExpanded ? '▼' : '►'}</span>
                  <span className="item-summary">{formatQueueItemSummary(item)}</span>
                </div>
                {isExpanded && renderItemDetails(item, sectionType)}
              </div>
            );
          })}
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="device-queue">
      <div className="queue-header">
        <h3>Media Request Queue</h3>
        {onRefresh && (
          <button 
            className="refresh-button" 
            onClick={onRefresh}
            disabled={refreshing}
            title="Refresh queue"
          >
            <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
          </button>
        )}
      </div>
      <div className="queue-grid">
        {renderQueueSection('Pending', Array.isArray(queue.pending) ? queue.pending : [], 'pending-section', 'pending')}
        {renderQueueSection('Processing', Array.isArray(queue.processing) ? queue.processing : [], 'processing-section', 'processing')}
        {renderQueueSection('Completed', Array.isArray(completedReversed) ? completedReversed : [], 'completed-section', 'completed')}
        {renderQueueSection('Failed', Array.isArray(failedReversed) ? failedReversed : [], 'failed-section', 'failed')}
      </div>
    </div>
  );
};

export default DeviceQueue;

